import React, { useEffect, useState } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";

/**
 * Banner "Installa app" per PWA su desktop/mobile.
 * - Su Chrome/Edge/Brave: appare un bottone reale che apre il prompt nativo
 * - Su Safari iOS/macOS: mostra istruzioni testuali (Safari non supporta beforeinstallprompt)
 * Dismissible con localStorage flag (24h).
 */
const DISMISS_KEY = "g360_pwa_install_dismissed_at";
const DISMISS_HOURS = 24;

export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    // Già installata?
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);
    if (standalone) return;

    // Dismiss recente?
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_HOURS * 3600 * 1000) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Detect iOS Safari (no beforeinstallprompt support)
    const ua = window.navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(ua);
    const isSafari = /safari/.test(ua) && !/chrome|crios|fxios/.test(ua);
    if (isIos && isSafari) {
      // Mostra hint dopo 3s
      const t = setTimeout(() => { setShowIosHint(true); setVisible(true); }, 3000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handler); };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  async function install() {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    }
    setDeferred(null);
  }

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  }

  if (isStandalone || !visible) return null;

  return (
    <div
      data-testid="install-pwa-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[60] bg-white border-2 border-[#0A0A0A] shadow-2xl p-4 sm:p-5 rounded-sm animate-in slide-in-from-bottom"
      style={{ animation: "slideUp 0.3s ease-out" }}
    >
      <button
        onClick={dismiss}
        aria-label="Chiudi"
        data-testid="install-pwa-dismiss"
        className="absolute top-2 right-2 p-1 text-[#64748B] hover:text-[#0A0A0A]"
      >
        <X size={16} weight="bold" />
      </button>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 bg-[#0033A0] text-white flex items-center justify-center rounded-sm font-black text-sm">
          SG
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#0033A0] font-bold mb-1">
            Installa l'app
          </div>
          <div className="font-display font-bold text-sm mb-1.5">
            Gestionale 360 sul tuo {showIosHint ? "iPhone/iPad" : "computer"}
          </div>
          {showIosHint ? (
            <p className="text-xs text-[#64748B] leading-relaxed">
              Tocca <strong>Condividi</strong> nella barra di Safari, poi{" "}
              <strong>"Aggiungi a Home"</strong>.
            </p>
          ) : (
            <p className="text-xs text-[#64748B] leading-relaxed">
              Aprila direttamente dal desktop, in finestra dedicata, senza browser.
            </p>
          )}
          {!showIosHint && deferred && (
            <button
              onClick={install}
              data-testid="install-pwa-btn"
              className="mt-3 btn-primary px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2"
            >
              <DownloadSimple size={14} weight="bold" /> Installa ora
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
